<script setup lang="ts">
import type { CheckoutResponse } from "#shared/types/core/ecommerce/checkout";
import { KsButton, KsCallout, KsIcon } from "@aschehoug/kloss";
import { faArrowRightLong } from "@fortawesome/pro-light-svg-icons";

const auth = useAuthorizeStore();

const orgName = computed(() => auth.user?.selectedOrganization?.name);

const cart = useCartStore();

const route = useRoute();

const { public: env } = useRuntimeConfig();

const vippsCheckoutData = ref<CheckoutResponse | undefined>();

const MUTATION_NAME = "checkout";

const { mutate: handlePayment, isPending: isPaying } = useMutation({
  mutationKey: [MUTATION_NAME],
  meta: {
    mutationName: MUTATION_NAME,
  },
  async mutationFn() {
    if (cart.isEmpty) {
      return null;
    }

    const res = await cart.checkout(route, env.hubspotPortalId);
    if (!res) {
      throw new Error("Failed to checkout");
    }
    return res;
  },
  onSuccess(data) {
    if (data === true) {
      navigateTo("/takk?source=invoice");
    } else if (data) {
      vippsCheckoutData.value = data;
    }
  },
});

const tosAccepted = ref(false);

onMounted(async () => {
  try {
    await cart.refreshRemoteCart();
  } catch (e) {
    handleError(e, "Failed to refresh basket", false);
  }
});

useHead({
  bodyAttrs: {
    class: "bg-beige-20",
  },
});
</script>

<template>
  <Content
    is="main"
    v-if="!auth.canUseCart"
    id="main-content"
    class="w-full"
  >
    <KsCallout
      class="mt-8"
      variant="warning"
      title="Du mangler tilgang til å se handlekurven."
    >
      <span class="mt-3.5">
        Ta kontakt med <NuxtLink to="/kundeservice">
          kundeservice
        </NuxtLink> om du
        mener dette er feil.
      </span>
    </KsCallout>
  </Content>
  <main
    v-else-if="cart.isEmpty"
    id="main-content"
    class="flex flex-col gap-4 sm:gap-10"
  >
    <LayoutBreadCrumbs :background="false" class="text-green-50" />

    <Content class="my-s w-full sm:my-m">
      <p
        v-if="auth.user?.selectedOrganization?.name"
        class="mb-6 font-headline text-3xl leading-snug text-green-50"
        v-text="auth.user?.selectedOrganization?.name"
      />

      <section class="bg-white px-6 py-l sm:px-24 xs:px-12 xs:py-xl">
        <h1 class="font-headline text-3xl leading-tight sm:text-[40px]">
          Handlekurven er tom
        </h1>
        <p class="mb-xl mt-s text-lg">
          <template v-if="auth.loggedIn">
            Det er ingen varer i handlekurven, fyll den opp med produkter fra vår produktkatalog.
          </template>
          <template v-else>
            Det er ingen varer i handlekurven. Logg inn for å se varer tilknyttet din profil, eller kjøp produktene våre.
          </template>
        </p>

        <div class="flex flex-col gap-m">
          <NuxtLink
            v-if="!auth.loggedIn"
            to="/login?redirectTo=/handlekurv"
            class="flex w-fit items-center gap-s font-semibold text-green-50 no-underline transition hover:translate-x-5"
          >
            <KsIcon :icon="faArrowRightLong" />
            Logg inn
          </NuxtLink>

          <NuxtLink to="/produktkatalog" class="flex w-fit items-center gap-s font-semibold text-green-50 no-underline transition hover:translate-x-5">
            <KsIcon :icon="faArrowRightLong" />
            Kjøp produkter
          </NuxtLink>
        </div>
      </section>
    </Content>
  </main>
  <Content
    is="main"
    v-else
    id="main-content"
    class="grid h-fit grid-cols-1 gap-s pb-8 pt-16 sm:grid-cols-[2fr,1fr] sm:gap-m"
  >
    <VippsCheckoutModal
      :data="vippsCheckoutData"
      @close="vippsCheckoutData = undefined"
    />
    <h1
      v-if="orgName"
      class="col-span-full h-fit font-headline text-3xl font-normal text-green-50"
      data-dd-privacy="mask"
      :aria-label="`Handlekurv for ${orgName}`"
      v-text="orgName"
    />
    <section class="bg-white p-8">
      <h2
        class="mb-8 inline-block text-xl font-medium uppercase tracking-[0.16rem] text-green-50"
      >
        Handlekurv
      </h2>
      ({{ cart.count }} produkter)
      <ul>
        <UiCartItem
          v-for="item in cart.items"
          :key="item.id"
          :item="item"
          @remove="cart.remove"
          @update-quantity="cart.updateAmount"
        />
      </ul>
    </section>
    <aside class="z-[1] flex h-fit flex-col gap-8 bg-white p-8 sm:sticky sm:top-28">
      <h2 class="text-xl uppercase tracking-[0.14rem]">
        Total sum
      </h2>
      <ul class="[&>li>span]:p-2 [&>li]:flex [&>li]:justify-between">
        <li>
          <span>Produkter</span>
          <span data-dd-privacy="mask" v-text="norwegianPrice(cart.summary.total, false)" />
        </li>
        <li v-if="cart.summary.discount !== 0" class="text-red-30">
          <span class="font-semibold">Du sparte</span>
          <span
            class="bg-coral-10"
            data-dd-privacy="mask"
            v-text="norwegianPrice(cart.summary.discount, false)"
          />
        </li>
        <li>
          <span>Herav mva.</span>
          <span data-dd-privacy="mask" v-text="norwegianPrice(cart.summary.tax, false)" />
        </li>
        <li class="mt-4 border-t border-t-beige-30 pt-4 font-semibold">
          <span>Totalt</span>
          <span data-dd-privacy="mask" v-text="norwegianPrice(cart.summary.total, false)" />
        </li>
      </ul>
      <div class="text-center">
        <input
          id="tos"
          v-model="tosAccepted"
          type="checkbox"
          name="tos"
        />
        <label for="tos">
          Jeg har lest og aksepterer
          <NuxtLink to="/kjopsbetingelser" target="_blank">
            kjøpsbetingelsene
          </NuxtLink>
        </label>
      </div>

      <!-- TODO: Find out why this have to be client-only -->
      <ClientOnly v-if="auth.isConsumer">
        <vipps-mobilepay-button
          brand="vipps"
          language="no"
          variant="primary"
          :rounded="true"
          verb="pay"
          :stretched="true"
          :branded="true"
          :loading="isPaying"
          :disabled="isPaying || !tosAccepted || cart.isEmpty"
          class="m-6 mt-0"
          @click="handlePayment"
        />
      </ClientOnly>
      <KsButton
        v-else
        variant="primary"
        shape="rounded"
        center
        :disabled="isPaying || !tosAccepted || cart.isEmpty"
        @click="handlePayment()"
      >
        Gjennomfør betaling
      </KsButton>

      <KsButton
        class="self-center"
        variant="secondary [--ks-secondary:theme(colors.black.80)]"
        @click="cart.clear()"
      >
        Tøm handlekurven
      </KsButton>
    </aside>
  </Content>
</template>
